import React, { useState } from 'react';
import styled from 'styled-components/macro';
import debounce from 'lodash/debounce';

import { distances } from '../../styles/constants';
import { InputValidation } from './';
import { SalesLocation } from '../../types/customers';
import Select from './SelectWithButton';
import { useTranslation } from 'react-i18next';

import { Label } from '../Typography';

export interface LocationsDropdownProps {
    value: SalesLocation | undefined;
    name: string;
    options: SalesLocation[];
    label: string;
    placeholder: string;
    onChange: (value: SalesLocation | undefined) => void;
    validation?: InputValidation;
}

const toLabel = (location: SalesLocation | undefined) =>
    location?.name || location?.business_name || location?.id || '';

const LocationsDropdown = (
    {
        label,
        name,
        value,
        options,
        onChange,
        placeholder,
    }: LocationsDropdownProps
) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState(value && toLabel(value));
    const [filteredOptions, setFilteredOptions] = useState(
        options.sort((a, b) => {
            return toLabel(a).localeCompare(toLabel(b));
        })
    );
    const searchOptions = (search: string) => {
        if (!search) {
            return setFilteredOptions(
                options.sort((a, b) => {
                    return toLabel(a).localeCompare(toLabel(b));
                })
            );
        }
        const result = options.filter((option: SalesLocation) => {
            const { name, business_name, location_id, organization_number, address } = option;
            const match = [
                name,
                business_name,
                location_id,
                organization_number,
                address?.address_line,
                address?.address_line_2,
                address?.postal_code,
                address?.postal_place,
            ]
                .filter((elem) => elem)
                .find((elem) => elem && elem.toLowerCase().indexOf(search.toLowerCase()) >= 0);
            return match;
        });
        return setFilteredOptions(result);
    };
    const debouncedSearch = debounce(searchOptions, 200);

    return (
        <Wrapper>
            <Label htmlFor={name}>{label}</Label>
            <Select
                initialValue={value}
                name={name}
                value={search}
                options={filteredOptions}
                getOptionLabel={toLabel}
                getOptionValue={(option) => option.id}
                getOptionSubLabel={(option: SalesLocation | undefined) =>
                    [
                        option?.organization_number,
                        option?.address?.address_line,
                        option?.address?.address_line_2,
                        option?.address?.postal_code,
                        option?.address?.postal_place,
                    ]
                        .filter((x) => x)
                        .join(', ')
                }
                onBlur={(e) => {
                    debouncedSearch.cancel();
                    if (!search) {
                        return;
                    }
                    if (search !== toLabel(value)) {
                        setSearch(toLabel(value));
                    }
                }}
                maxOptions={10}
                maxOptionsReachedMessage={t('payments.virtual_terminal.fields.locations.max_reached', {
                    total: options.length,
                    limit: 10,
                })}
                noOptionsMessage={t('payments.virtual_terminal.fields.locations.no_options')}
                autoFocus={false}
                onInputChange={(e) => {
                    if (!e.target.value) {
                        setSearch('');
                        onChange(undefined);
                    }
                    setSearch(e.target.value);
                    debouncedSearch(e.target.value);
                }}
                onChange={(selection: SalesLocation) => {
                    setSearch(toLabel(selection));
                    onChange(selection);
                    debouncedSearch.cancel();
                }}
                placeholder={placeholder}
                hideOnNoOptions={false}
            />
        </Wrapper>
    );
};

const Wrapper = styled.div`
    position: relative;
    width: 100%;
`;

export default LocationsDropdown;
